/**
 * useTokenRefresh — Token 主动刷新 composable
 * 定时检查 Token 过期时间，剩余 < 5 分钟时主动刷新
 * 监听 Tauri 事件处理 Token 刷新结果
 *
 * 在 App.vue 中使用：
 *   useTokenRefresh()
 */
import { onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { ipcCall } from '@/composables/useIPC'
import { listenEvent } from '@/composables/useEvent'
import { IpcCommand, TauriEvent } from '@/types/ipc'
import { logger } from '@/utils/logger'
import type { UnlistenFn } from '@tauri-apps/api/event'

const CHECK_INTERVAL = 60_000 // 60 秒检查一次
const REFRESH_THRESHOLD = 5 * 60_000 // 提前 5 分钟刷新

export function useTokenRefresh() {
  let timer: ReturnType<typeof setInterval> | null = null
  const unlisteners: UnlistenFn[] = []

  /** 检查 Token 是否需要刷新 */
  async function checkAndRefresh() {
    const authStore = useAuthStore()
    if (!authStore.isLoggedIn) return

    try {
      // 解析 JWT payload 获取过期时间
      const payload = parseJwtPayload(authStore.token)
      if (!payload?.exp) return

      const expiresAt = payload.exp * 1000 // JWT exp 是秒级时间戳
      const remaining = expiresAt - Date.now()

      if (remaining < REFRESH_THRESHOLD && remaining > 0) {
        logger.info('TokenRefresh', '令牌即将过期，主动刷新', {
          remaining: Math.round(remaining / 1000) + 's',
        })
        const result = await ipcCall<{ success: boolean; token?: string }>(
          IpcCommand.AuthRefreshToken,
        )
        if (result?.success && result.token) {
          // 浏览器开发环境不会派发 Tauri 事件，直接同步刷新结果；桌面端重复设置无副作用。
          authStore.setToken(result.token)
          logger.info('TokenRefresh', 'Token 已刷新')
        }
      }
    } catch (err) {
      logger.warn('TokenRefresh', '检查 Token 过期时间失败', err)
    }
  }

  /** 解析 JWT payload（不验签，仅解码） */
  function parseJwtPayload(token: string): { exp?: number } | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null
      const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(payload)
    } catch {
      return null
    }
  }

  /** 监听 Rust 端 Token 刷新事件 */
  async function setupEventListeners() {
    const authStore = useAuthStore()

    // Token 刷新成功 → 更新 Pinia Token
    const unlistenRefreshed = await listenEvent(TauriEvent.AuthTokenRefreshed, (payload) => {
      if (payload.token) {
        authStore.setToken(payload.token)
        logger.info('TokenRefresh', 'Token 已刷新')
      }
    })
    unlisteners.push(unlistenRefreshed)

    // Token 过期（刷新失败）→ 清除登录态，跳转登录页
    // 注意：弹窗由 client.ts handleTokenExpired() → window('token-expired') → App.vue 处理
    // 此处仅作为兜底清理：如果弹窗未触发（极端情况），至少清除登录态并跳转
    const unlistenExpired = await listenEvent(TauriEvent.AuthTokenExpired, () => {
      logger.warn('TokenRefresh', 'Token 已过期，需要重新登录')
      // 仅当 client.ts 未派发 token-expired 事件时（5 秒后仍停留在当前页）才执行清理
      setTimeout(() => {
        const authStore = useAuthStore()
        if (authStore.isLoggedIn) {
          logger.warn('TokenRefresh', 'Token 过期弹窗未触发，执行兜底清理')
          authStore
            .logout()
            .catch((e) => logger.warn('TokenRefresh', 'Token 过期兜底登出失败', e))
            .finally(() => {
              window.location.hash = '#/login'
            })
        }
      }, 5000)
    })
    unlisteners.push(unlistenExpired)
  }

  onMounted(() => {
    setupEventListeners()
    // 启动定时检查
    timer = setInterval(checkAndRefresh, CHECK_INTERVAL)
    // 立即检查一次
    checkAndRefresh()
  })

  onUnmounted(() => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    unlisteners.forEach((fn) => fn())
    unlisteners.length = 0
  })
}
